import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button, Toast } from '@/nova/components';
import type { ToastData } from '@/nova/components/Toast/Toast';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import { useAppDispatch, useAppSelector } from '@/store';
import { creditCostFor, fetchCreditsPricing } from '@/store/slices/aiCredits';

import { useActiveArtStyleState } from '../../../art-style/useActiveArtStyle';
import { brandLogoSource } from '../../../casino-settings/logos';
import { useBrandSettled } from '../../../website/useBrandSettled';
import { adLogoSet, gameLogos, type GameLogos } from '../../ad-subjects';
import { firstBrandLogoUrl } from '../../brand-logo';
import type { CreativeItem } from '../../creative-api';
import { useBrandPromotionPool } from '../../hooks/useBrandPromotionPool';
import { fetchPlayableTemplates, isHtml5Banner, isMiniGame, type PlayableTemplatesResponse } from '../../playable-api';
import type { TrackingLinkKind } from '../../tracking-links-api';
import { useBannerKitLibrary } from '../../useBannerKitLibrary';
import { usePlayableLibrary } from '../../usePlayableLibrary';
import { useVideoLibrary } from '../../useVideoLibrary';
import { useVideoReopenSubject } from '../../useVideoReopenSubject';
import { pipelineOf } from '../../video-library-api';
import { BannerKitComposer } from '../BannerKitComposer';
import { BuyCreditsSheet } from '../BuyCreditsSheet';
import { LandingPageComposer } from '../LandingPageComposer';
import { useLandingPageLibrary } from '../LandingPageLibrary/useLandingPageLibrary';
import { useLivePages } from '../LandingPageLibrary/useLivePages';
import { PlayableComposer } from '../PlayableComposer';
import { StoryboardComposer } from '../StoryboardComposer';
import * as S from './AssetComposerOverlay.styles';

export interface AssetComposerOverlayProps {
  kind: TrackingLinkKind;
  creativeId: string;
  /** The composer (or the dialog) closed. */
  onClose: () => void;
  /** Leave for the asset's own page (the "can't be opened here" dialog). */
  onOpenPage: () => void;
  /** Show a toast (the composer's notices, a credits purchase). */
  onNotify: (message: string) => void;
}

/** What shows while the asset is not open: it is loading, it is not there, or it could not be read. */
type Pending = 'loading' | 'missing' | 'failed';

/** The libraries' own "not found" notices (already in every catalog), by kind. */
const NOT_FOUND: Record<TrackingLinkKind, string> = {
  video: "That video ad couldn't be found.",
  kit: "That image banner couldn't be found.",
  playable: "That mini-game couldn't be found.",
  banner: "That HTML5 banner couldn't be found.",
  landing_page: "That landing page couldn't be found.",
};

/** The active brand as the Marketing Assets libraries get it from PageCreatives (same helpers, same slots). */
interface OverlayBrand {
  tenantId: number;
  brandName: string;
  brandDomain?: string;
  /** The brand's first logo (the video composer's). */
  brandLogoUrl?: string;
  /** Every logo variant by slot (Image Banners place the one that fits each size). */
  adBrandLogos: Record<string, string>;
  /** The mini-game logos (primary logo and bare symbol). */
  adGameLogos: GameLogos;
}

/** What every per-kind opener gets. */
interface KindProps {
  id: string;
  brand: OverlayBrand;
  items: CreativeItem[];
  itemsLoaded: boolean;
  /** The credit price table has settled (the composers price every render from it). */
  pricingLoaded: boolean;
  onClose: () => void;
  onBuyCredits: (needed?: number) => void;
  onNotify: (message: string) => void;
  /** The frame every opener renders: the composer once open, else the loading cue or the "can't open" dialog. */
  frame: (state: Pending | 'open', composer?: React.ReactElement) => React.ReactElement;
}

/** The loading cue: a toast pill (non-modal), never a Sheet of its own (see OpenerFrame). */
const LOADING_TOAST: ToastData = { message: 'Loading…', token: 1 };

/** How long a load runs before its cue shows: a fast read opens the composer with no cue at all. A wait on the
 *  network, not an animation. */
const LOADING_CUE_AFTER_MS = 300;

/**
 * What an opener renders, ONE element type whatever the state, so React keeps it (and its toast) mounted while the
 * state moves on. The composer's own Sheet is the only Sheet that opens for an asset: a loading Sheet that the
 * composer's replaced would unmount without its exit and the composer's would slide in again (an open, a close, a
 * second open). So while the asset loads there is only a toast pill, which animates out as the composer opens; an
 * asset that is not there opens the small "can't be opened here" dialog instead, once.
 */
const OpenerFrame = ({
  state,
  kind,
  composer,
  onClose,
  onOpenPage,
}: {
  state: Pending | 'open';
  kind: TrackingLinkKind;
  composer?: React.ReactElement;
  onClose: () => void;
  onOpenPage: () => void;
}): React.ReactElement => {
  const { t } = useTranslation();
  const { closeHandle, dismiss } = useSheetDismiss(onClose);
  const loading = state === 'loading';
  // Syncs with a timer: the cue shows only once a load has run past the grace period.
  const [cue, setCue] = React.useState(false);
  React.useEffect(() => {
    if (!loading) {
      setCue(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setCue(true), LOADING_CUE_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [loading]);
  return (
    <>
      {state === 'open' ? composer : null}
      <Toast toast={loading && cue ? LOADING_TOAST : null} />
      {state === 'missing' || state === 'failed' ? (
        <Sheet
          title={state === 'failed' ? "That asset couldn't be loaded right now." : NOT_FOUND[kind]}
          onClose={onClose}
          closeHandle={closeHandle}
          maxWidth="460px"
          footer={
            <S.MissingActions>
              <Button variant="text" label={t('Close')} onClick={dismiss} />
              <Button variant="primary" label={t('Open its page')} onClick={onOpenPage} />
            </S.MissingActions>
          }
        >
          <S.MissingText text="You can still try its own page in Marketing Assets." />
        </Sheet>
      ) : null}
    </>
  );
};

/** An image banner (banner kit): resolved from the brand's kits, as Image Banners does (there is no single read). */
const KitOpener = ({ id, brand, items, pricingLoaded, onClose, onBuyCredits, onNotify, frame }: KindProps): React.ReactElement => {
  const { items: kits, loading, loaded, listFailed, patch } = useBannerKitLibrary(brand.tenantId);
  const kit = kits.find((k) => k.id === id);
  if (!kit) return frame(!loaded || loading ? 'loading' : listFailed ? 'failed' : 'missing');
  if (!pricingLoaded) return frame('loading');
  return frame(
    'open',
    <BannerKitComposer
      key={`${brand.tenantId}:${kit.id}`}
      tenantId={brand.tenantId}
      kit={kit}
      brandName={brand.brandName}
      brandDomain={brand.brandDomain}
      brandLogos={brand.adBrandLogos}
      items={items}
      patch={patch}
      onClose={onClose}
      onBuyCredits={onBuyCredits}
      onNotify={onNotify}
    />,
  );
};

/** A mini-game or an HTML5 banner: both are playables, told apart by their template (the registry), as each library
 *  does. */
const PlayableOpener = ({
  kind,
  id,
  brand,
  items,
  pricingLoaded,
  onClose,
  onBuyCredits,
  onNotify,
  frame,
}: KindProps & { kind: 'playable' | 'banner' }): React.ReactElement => {
  const { items: playables, loading, loaded, listFailed, patch } = usePlayableLibrary(brand.tenantId);
  const [registry, setRegistry] = React.useState<PlayableTemplatesResponse | null>(null);
  const [registryFailed, setRegistryFailed] = React.useState(false);
  React.useEffect(() => {
    // Syncs with spark-creatives: the template registry (static per deploy, cached by the client).
    let live = true;
    void fetchPlayableTemplates().then((r) => {
      if (!live) return;
      if (r) setRegistry(r);
      else setRegistryFailed(true);
    });
    return () => {
      live = false;
    };
  }, []);
  const isBanner = kind === 'banner';
  // Without the registry no playable can be told to be a mini-game or a banner.
  if (registryFailed) return frame('failed');
  const playable = registry
    ? playables.find(
        (p) => p.id === id && (isBanner ? isHtml5Banner(p, registry.templates) : isMiniGame(p, registry.templates)),
      )
    : undefined;
  if (!playable) return frame(!registry || !loaded || loading ? 'loading' : listFailed ? 'failed' : 'missing');
  if (!pricingLoaded) return frame('loading');
  return frame(
    'open',
    isBanner ? (
      <PlayableComposer
        key={`${brand.tenantId}:${playable.id}`}
        kind="banner"
        tenantId={brand.tenantId}
        playable={playable}
        brandName={brand.brandName}
        brandDomain={brand.brandDomain}
        items={items}
        patch={patch}
        onClose={onClose}
        onBuyCredits={onBuyCredits}
        onNotify={onNotify}
      />
    ) : (
      <PlayableComposer
        key={`${brand.tenantId}:${playable.id}`}
        tenantId={brand.tenantId}
        playable={playable}
        brandName={brand.brandName}
        brandDomain={brand.brandDomain}
        gameLogos={brand.adGameLogos}
        items={items}
        patch={patch}
        onClose={onClose}
        onBuyCredits={onBuyCredits}
        onNotify={onNotify}
      />
    ),
  );
};

/** A video ad: resolved from the brand's ads, its subject inferred as Video Ads does (useVideoReopenSubject). Opened in
 *  the easy modal: the advanced canvas is the Video Ads route's own (`?mode=advanced`, the immersive layout). */
const VideoOpener = ({
  id,
  brand,
  items,
  itemsLoaded,
  pricingLoaded,
  onClose,
  onBuyCredits,
  onNotify,
  frame,
}: KindProps): React.ReactElement => {
  const { videos, loading, loaded, listFailed, patch } = useVideoLibrary(brand.tenantId);
  const reopen = videos.find((v) => v.id === id);
  const { subjectType, composerItems, settled } = useVideoReopenSubject({
    reopen,
    subject: null,
    subjectItems: null,
    items,
    itemsLoaded,
  });
  // A mature primary art style blocks video renders (the library's read; unknown until it settles, failing closed).
  const { active: activeStyle, loaded: styleLoaded } = useActiveArtStyleState();
  const beatCost = useAppSelector((state) => creditCostFor(state.aiCredits?.pricing, 'video_beat'));
  if (!reopen) return frame(!loaded || loading ? 'loading' : listFailed ? 'failed' : 'missing');
  if (!settled || !pricingLoaded) return frame('loading');
  return frame(
    'open',
    <StoryboardComposer
      key={`${brand.tenantId}:${reopen.id}`}
      tenantId={brand.tenantId}
      brandName={brand.brandName}
      brandDomain={brand.brandDomain}
      brandLogoUrl={brand.brandLogoUrl}
      items={composerItems}
      subjectType={subjectType}
      pipeline={pipelineOf(reopen.storyboard?.pipeline)}
      beatCost={beatCost}
      mode="easy"
      reopen={reopen}
      onClose={onClose}
      onBuyCredits={onBuyCredits}
      onNotify={onNotify}
      onEntityPatch={patch}
      matureStyle={Boolean(activeStyle?.mature)}
      styleChecking={!styleLoaded}
    />,
  );
};

/** A landing page: resolved from the brand's pages, with its live pages read, as Landing Pages does. Its links to other
 *  Studio pages (the asset it was made for, a link's asset) leave for that page. */
const LandingOpener = ({
  id,
  brand,
  items,
  pricingLoaded,
  onClose,
  onBuyCredits,
  onNotify,
  frame,
}: KindProps): React.ReactElement => {
  const navigate = useNavigate();
  const { items: pages, loading, loaded, listFailure, patch } = useLandingPageLibrary(brand.tenantId);
  const { live, failed: liveFailed, reload: reloadLive } = useLivePages(brand.tenantId);
  const page = pages.find((p) => p.id === id);
  if (!page) {
    if (!loaded || loading) return frame('loading');
    // A failed read is not an answer (a 404 means this environment keeps no landing pages: the page is not there).
    return frame(listFailure !== null && listFailure !== 404 ? 'failed' : 'missing');
  }
  if (!pricingLoaded) return frame('loading');
  return frame(
    'open',
    <LandingPageComposer
      // A brand switch remounts the composer: one mounted across it would save this page under the new brand.
      key={`${brand.tenantId}:${page.id}`}
      tenantId={brand.tenantId}
      page={page}
      brandName={brand.brandName}
      brandDomain={brand.brandDomain}
      items={items}
      live={live}
      liveFailed={liveFailed}
      patch={patch}
      onLiveChanged={reloadLive}
      onClose={onClose}
      onBuyCredits={onBuyCredits}
      onNotify={onNotify}
      onNavigate={(path) => navigate(path)}
    />,
  );
};

/**
 * One asset opened IN PLACE (over the current page) in the same composer its Marketing Assets library opens it in, with
 * the same wiring: the brand resolved from `displayBrand` the way PageCreatives does, the library's own list hook (its
 * optimistic `patch`, its job-driven refresh, its persistence through the composer), the brand's promotion pool, the
 * credit price table, Buy credits and toasts. The composer's own Sheet is the only one that opens: while the asset loads
 * a toast pill says so (OpenerFrame); when it cannot be opened, a small dialog offers its own page. Mounted by
 * {@link useAssetOverlay}.
 */
export const AssetComposerOverlay = ({
  kind,
  creativeId,
  onClose,
  onOpenPage,
  onNotify,
}: AssetComposerOverlayProps): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const brandSettled = useBrandSettled();

  // The active brand: the lagged displayBrand, resolved exactly as PageCreatives resolves it for the libraries.
  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand);
  const tenantRaw = Number(displayBrand?.tenant_id);
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;
  const brandName = displayBrand?.casino_data?.details?.brand ?? '';
  const brandDomain = displayBrand?.casino_data?.details?.domain;
  const brandLogoUrl = firstBrandLogoUrl(displayBrand?.casino_data?.details?.logo);
  const adBrandLogos = React.useMemo(() => adLogoSet(brandLogoSource(displayBrand ?? undefined)), [displayBrand]);
  const adGameLogos = React.useMemo(
    () => gameLogos(brandLogoSource(displayBrand ?? undefined), brandLogoUrl),
    [displayBrand, brandLogoUrl],
  );
  const brand = React.useMemo<OverlayBrand | null>(
    () => (tenantId ? { tenantId, brandName, brandDomain, brandLogoUrl, adBrandLogos, adGameLogos } : null),
    [tenantId, brandName, brandDomain, brandLogoUrl, adBrandLogos, adGameLogos],
  );

  const pool = useBrandPromotionPool(tenantId, brandDomain);

  // The credit price table (spark-owned): every composer prices its renders from it. Pages outside Marketing Assets
  // never read it, so it is read here (settles on failure too).
  const pricingLoaded = useAppSelector((state) => state.aiCredits?.pricingLoaded ?? false);
  React.useEffect(() => {
    void dispatch(fetchCreditsPricing());
  }, [dispatch]);

  // Buy credits: the shortfall a 402 reported preselected, as on the Creatives page.
  const [buy, setBuy] = React.useState<{ suggested?: number } | null>(null);
  const openBuy = React.useCallback((needed?: number): void => {
    setBuy({ suggested: needed && needed > 0 ? needed : undefined });
  }, []);

  const frame = (state: Pending | 'open', composer?: React.ReactElement): React.ReactElement => (
    <OpenerFrame state={state} kind={kind} composer={composer} onClose={onClose} onOpenPage={onOpenPage} />
  );

  const opener = ((): React.ReactElement => {
    // No brand to open it for: none will resolve, or the selected one has no usable tenant id.
    if (!brand) return frame(brandSettled ? 'failed' : 'loading');
    const props: KindProps = {
      id: creativeId,
      brand,
      items: pool.items,
      itemsLoaded: pool.loaded,
      pricingLoaded,
      onClose,
      onBuyCredits: openBuy,
      onNotify,
      frame,
    };
    // Keyed by tenant: a brand switch remounts the opener, so its list starts empty instead of still holding the
    // previous brand's entities (one found there would open its composer under the new brand).
    const key = brand.tenantId;
    switch (kind) {
      case 'kit':
        return <KitOpener key={key} {...props} />;
      case 'playable':
      case 'banner':
        return <PlayableOpener key={key} {...props} kind={kind} />;
      case 'video':
        return <VideoOpener key={key} {...props} />;
      default:
        return <LandingOpener key={key} {...props} />;
    }
  })();

  return (
    <>
      {opener}
      {buy ? (
        <BuyCreditsSheet
          suggestedCredits={buy.suggested}
          onClose={() => setBuy(null)}
          onSuccess={() => onNotify(t('Credits added to your balance.'))}
        />
      ) : null}
    </>
  );
};

export default AssetComposerOverlay;
